import BaseViewService from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-service';
import { IBaseCanvasProps, IBaseCanvasService } from '@src/slide/application/service/canvas-component/core/base/type';
import {
    IBaseCanvasAsyncServiceDelegates,
    IBaseCanvasEditServiceDelegates,
    IBaseCanvasServiceDelegates,
    IBaseCanvasViewerServiceDelegates,
    IBaseNoteAreaRef,
} from '@src/slide/application/service/canvas-component/core/base/base-canvas-service.interface';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import BaseCanvasModel from '@src/slide/application/service/canvas-component/core/base/base-canvas-model';
import {
    ICanvasSize,
    ICanvasSizeHandler,
} from '@src/slide/application/service/canvas-component/core/base/canvas-size-handler/interface';
import { RefName } from '@editor/src/page/index/components/canvas/canvas-constants';
import { getFontScale } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils';
import {
    IMMEDIATELY_RESIZE,
    PPT_RESIZE,
    SCROLL_TO_CENTER,
} from '@editor/src/page/index/components/canvas/canvas-custom-event-name';
import { reaction } from 'mobx';
import expect from '@src/slide/infrastructure/module/independent/assert/expect';
import ColorPicker from '@com-pc/color-picker';
import { IBaseCanvasInjectable } from '@src/slide/application/service/canvas-component/inject/base/interface';
import { IBaseNoteAreaInfo } from '../../../note-area-component/core/base/base-note-area-service.interface';

declare global {
    interface Window {
        intoPlayForResize: boolean;
        lastWH: string;
        fontScaleFactor: number;
    }
}

export default abstract class BaseCanvasService<P extends IBaseCanvasProps> extends BaseViewService<P>
    implements IBaseCanvasService {
    declare protected model: BaseCanvasModel<P>;

    protected canvasSizeHandler: ICanvasSizeHandler;
    protected isHeaderFold: boolean;
    protected pageScale: number;
    // service
    protected services?: IBaseCanvasServiceDelegates;
    // inject
    protected inject?: IBaseCanvasInjectable;

    private slideWidth: number;
    private slideHeight: number;
    private timer;
    private toolbarMode: string;
    private curSlide: any;
    private slideChildrenLen: any;

    public constructor(inject?: IBaseCanvasInjectable) {
        super();
        this.inject = inject;
        this.canvasSizeHandler = this.createCanvasSizeHandler();
    }

    public setViewerServiceDelegates(services: IBaseCanvasViewerServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
        this.canvasSizeHandler.setServices(services);
    }

    public setEditServiceDelegates(services: IBaseCanvasEditServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
        this.canvasSizeHandler.setServices(services);
    }

    public setAsyncServiceDelegates(services: IBaseCanvasAsyncServiceDelegates) {
        this.services = {
            ...this.services,
            ...services,
        };
        this.canvasSizeHandler.setServices(services);
    }

    public getServices(): IBaseCanvasServiceDelegates | undefined {
        return this.services;
    }

    public getPresentationSize(): { slideWidth: number, slideHeight: number } {
        const { slideWidth = 0, slideHeight = 0 } = this.services?.uiDataService?.presentation.props || { };
        return { slideWidth, slideHeight };
    }

    public componentDidMount(): void {
        window.addEventListener('resize', this.resizeDelay);
        window.addEventListener(PPT_RESIZE, this.resizeDelay);
        window.addEventListener(IMMEDIATELY_RESIZE, this.resize);
        window.addEventListener(SCROLL_TO_CENTER, () => {
            this.slideScrollCenter(this.model.canvasSize); // slide显示居中
        });
        document.body.addEventListener('touchmove', this.onMaskTouchMove, { passive: false });

        this.slideScrollCenter(this.model.canvasSize); // slide显示居中

        this.viewDelegate?.disposeOnUnmount(reaction(() => {
            const { slideWidth, slideHeight } = this.getPresentationSize(); // 监听slide w和h变更
            const curSlide = this.services?.selectionService?.currentSlide; // 监听slide变更
            const headerFold = this.services?.toolbarStateManager?.isHeaderFold; // 监听header折叠的变更
            const toolbarMode = this.services?.toolbarStateManager?.getToolbarMode();

            return {
                slideWidth,
                slideHeight,
                curSlide,
                headerFold,
                toolbarMode,
            };
        }, (obj) => {
            this.handleSizeChange(obj);
        }));

        this.props?.canvasDidMountCallback?.();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.resizeDelay);
        window.removeEventListener('PPT-resize', this.resizeDelay);
        window.removeEventListener('immediately-resize', this.resize);
        document.body.removeEventListener('touchmove', this.onMaskTouchMove);
    }

    public handleMouseDown = () => {
        if (ColorPicker.isShown()) return;
        this.deselect();
    };

    public getPageScale = () => this.pageScale;

    public getCanvasSizeHandler(): ICanvasSizeHandler {
        return this.canvasSizeHandler;
    }

    /**
     * 获取备注尺寸相关信息
     */
    public getNoteAreaInfo(): IBaseNoteAreaInfo {
        return this.noteAreaRef?.getNoteAreaInfo() ?? this.getDefaultNoteAreaInfo();
    }

    public isDraggingElement = (): boolean => !!this.services?.editingStateManager?.isDraggingElement;

    public resize = (e?, scrollCenter?, { noteHeightChange = true } = {}) => {
        // player限频逻辑
        if (window.pray().isPlaying) {
            const wh = `${document.documentElement.clientWidth}_${document.documentElement.clientHeight}`;
            const screenWH = `${window.screen.width}_${window.screen.height}`;
            if (wh === screenWH || wh === window.lastWH || window.intoPlayForResize) {
                window.intoPlayForResize = false;
                return;
            }
        }

        // 拖拽过程中无需resize
        if (this.services?.editingStateManager?.isDragging) {
            return;
        }

        this.quickSettingUpdateRect(e);

        const canvasSize = this.updateCanvasSize();
        if (noteHeightChange) {
            this.fixNoteHeightByResize();
        }

        const scale = this.services?.editAreaCanvasSizeManager?.getCanvasSize();
        if (!this.canvasSizeHandler.canvasSizeChange(canvasSize, scale, this.containerDom)) {
            return;
        }
        this.updateModelAndScroll(canvasSize, scrollCenter).then(() => {
            // 在切换缩放比时，由于计算尺寸部分环节依赖了获取dom|canvas元素，所以画布边界会因为上一次的延迟而计算失准，所以需要在元素绘制完成后再重算一次
            if (e?.detail?.countSizeAgain) {
                const canvasSizeAgain = this.updateCanvasSize();
                if ((canvasSizeAgain.width !== canvasSize.width) || (canvasSizeAgain.height !== canvasSize.height)) {
                    this.updateModelAndScroll(canvasSizeAgain, scrollCenter);
                }
            }
            if (e?.detail?.cb) {
                e.detail.cb();
            }
        });
    };

    protected initWithProps(props?: P) {
        const { canvasSize } = this.model;
        this.updateSlideSize();
        this.updateDisableScroll(canvasSize);
        this.services?.editAreaCanvasSizeManager?.setCanvasSize({
            canvasWidth: canvasSize?.width,
            canvasHeight: canvasSize?.height,
            slideLeft: canvasSize?.left,
            slideTop: canvasSize?.top,
            scale: canvasSize?.scale,
        });
        this.pageScale = canvasSize?.scale ?? 1;
        this.isHeaderFold = !!this.inject?.isHeaderFold();
        this.updateFontScale();
    }

    protected createViewModel(): BaseViewModel<IBaseCanvasProps> {
        return new BaseCanvasModel();
    }

    protected getInitModelValue(): Partial<BaseCanvasModel<P>> | undefined {
        return {
            canvasSize: this.canvasSizeHandler.getCanvasSize(
                this.isHeaderFold ?? this.inject?.isHeaderFold(),
                this.getDefaultNoteAreaInfo(),
                this.inject?.isStatusBarFold(),
            ),
        };
    }

    protected updateSlideSize() {
        const { slideWidth, slideHeight } = this.getPresentationSize();
        this.slideWidth = slideWidth;
        this.slideHeight = slideHeight;
    }

    protected updateDisableScroll = (newCanvasSize) => {
        const { offsetHeight, offsetWidth } = this.canvasSizeHandler
            .getCanvasOffsetHeightWidth(this.getHeaderFold(), this.getNoteAreaInfo(), this.inject?.isStatusBarFold());

        this.model.disableScrollX = newCanvasSize.width <= offsetWidth;
        this.model.disableScrollY = newCanvasSize.height <= offsetHeight;
    };

    protected updateCanvasSize() {
        const canvasSize = this.canvasSizeHandler.getCanvasSize(
            this.getHeaderFold(),
            this.getNoteAreaInfo(),
            this.inject?.isStatusBarFold(),
        );
        this.updateSlideSize();
        this.updateDisableScroll(canvasSize);
        return canvasSize;
    }

    protected getHeaderFold() {
        return this.isHeaderFold;
    }

    /**
     * 是否触发resize方法。可以被子类重写
     * @protected
     */
    protected shouldResize(): boolean {
        return true;
    }

    /**
     * resize延时时间。子类可重写
     * @protected
     */
    protected getResizeDelayTime(): number {
        return 0;
    }

    protected scrollToEndEditing(): void {
        // 子类可重写
    }

    protected get containerDom() {
        return this.viewDelegate?.getElementById(RefName.CONTAINER);
    }

    protected get noteAreaRef(): IBaseNoteAreaRef | undefined {
        const noteAreaRef = this.viewDelegate?.getElementById(RefName.NOTE_AREA);
        return noteAreaRef as IBaseNoteAreaRef;
    }

    protected deselect() {
        this.services?.selectionService?.selectedElements.clear();

        this.services?.editingStateManager?.updateElementCropState(false);
        this.services?.editingStateManager?.updateElementResizeState(false);
        this.services?.editingStateManager?.updateElementDraggingState(false);
        this.services?.editingStateManager?.updateElementRotateState(false);
    }

    protected hideFloatBar() {
        this.services?.linkManager?.linkFloatBarService.hide();  // 关闭连接浮层
    }

    // resize限频
    protected resizeDelay = (e, scrollCenter?) => {
        // 备注全屏编辑时不resize
        if (!this.shouldResize()) {
            return;
        }
        if (this.timer) {
            clearTimeout(this.timer);
        }

        const delayTime = this.getResizeDelayTime();
        this.timer = setTimeout(() => {
            const isScrollCenter = expect(scrollCenter).isUndefined() ? e?.detail?.scrollCenter : scrollCenter;
            this.resize(e, isScrollCenter);
            this.timer = null;
        }, delayTime);
    };

    // 查看是否有字体缩放
    private updateFontScale = () => {
        window.fontScaleFactor = getFontScale();
    };

    // 更新QuickSetting（降低圈复杂度，提出来）
    private quickSettingUpdateRect = (e) => {
        if (e?.type === 'resize' || e?.type === 'PPT-resize') {
            import(/* webpackChunkName: "service" */ '@com/quick-setting/quick-setting-manager')
                .then((module) => {
                    const QuickSettingManager = module.default;
                    QuickSettingManager.getInstance().updateAnchorRect();
                })
                .catch((error) => {
                    window.log.error(`[loadChunk-error]${error}`);
                });
        }
    };

    // 用计算处理的新的canvasSize进行数据更新
    private updateModelAndScroll = (canvasSize: ICanvasSize, scrollCenter) => new Promise<void>((resolve) => {
        this.services?.editAreaCanvasSizeManager?.setCanvasSize({
            canvasWidth: canvasSize.width,
            canvasHeight: canvasSize.height,
            slideLeft: canvasSize.left,
            slideTop: canvasSize.top,
            scale: canvasSize.scale,
        });
        this.pageScale = canvasSize.scale;
        this.updateModel<BaseCanvasModel<P>>({
            canvasSize,
        }, () => {
            if (scrollCenter) {
                this.slideScrollCenter(canvasSize);
            } else if (this.services?.editorInstanceService?.checkEditorIsEditing()) {
                this.scrollToEndEditing();
            }
            resolve();
        });
    });

    /**
     * slide页面滚动到中心位置
     * @param canvasSize
     */
    private slideScrollCenter = (canvasSize) => {
        const canvasDom = this.containerDom;
        if (canvasDom) {
            canvasDom.scrollLeft = 0;
            canvasDom.scrollTop = 0;
            canvasDom.style.overflow = null;
            if (!this.model.disableScrollX) { // x轴滚动条位置调整
                canvasDom.scrollLeft = -canvasSize.cLeft;
            }
            if (!this.model.disableScrollY) { // Y轴滚动条位置调整
                canvasDom.scrollTop = -canvasSize.cTop;
            }
        }
    };

    private handleSizeChange = (obj) => {
        const {
            slideWidth,
            slideHeight,
            curSlide,
            headerFold,
            toolbarMode,
        } = obj;
        const children = curSlide?.children || [];
        const cLen = children.length;
        this.hideFloatBar();
        if (this.isSlideSizeChange(slideWidth, slideHeight, headerFold, toolbarMode)) {
            this.isHeaderFold = headerFold;
            this.toolbarMode = toolbarMode;
            if (curSlide) {
                // 选中 slide 变化，就需要把 slide 页面滚动到中心位置
                const scrollCenter = curSlide !== this.curSlide;
                this.resize(undefined, scrollCenter);  // 如果发现slide大小变更，触发一次resize
            }
        } else if (curSlide) {
            if (curSlide !== this.curSlide) {  // 切换幻灯片，resize一次
                this.curSlide = curSlide;
                this.slideChildrenLen = cLen; // 切换slide重置
                // canvas切页时在resize后，所以为了获取切页后图形大小 需要在渲染后再次计算
                this.resize({ detail: { countSizeAgain: true } }, true);
            } else if (cLen !== this.slideChildrenLen) {
                this.slideChildrenLen = cLen;
                this.resize();
            }
        }
    };

    private isSlideSizeChange(slideWidth, slideHeight, headerFold, toolbarMode) {
        return this.slideWidth !== slideWidth
            || this.slideHeight !== slideHeight
            || this.isHeaderFold !== headerFold
            || (this.toolbarMode ?? 'none') !== (toolbarMode ?? 'none'); // 兜底一个是undefined，一个是null
    }

    private onMaskTouchMove = (e) => {
        // 图形和连接线拖拽点的touchmouve绑定在docuemnt上，当拖动时禁掉body的touchmove事件，进而使得内部slide容器不能滚动（部分浏览器特性）
        // 从而避免在移动端slide容器存在滚动条时拖拽，出现图形和滚动条一起动的bug
        if (this.isDraggingElement() || this.services?.editingStateManager?.isConnecting) {
            e.preventDefault();
        }
    };

    public abstract handleScrollEvent(e): void;
    protected abstract createCanvasSizeHandler(): ICanvasSizeHandler;
    protected abstract getDefaultNoteAreaInfo(): IBaseNoteAreaInfo;
    protected abstract fixNoteHeightByResize(): void;
}
